import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

export const WORKSPACE_CHECKBOX_COL_WIDTH_PX = 44;
export const WORKSPACE_CHECKBOX_COL_SIZE_CLASS = 'w-[44px] min-w-[44px] max-w-[44px]';
/** Shared by header, body, and footer so the checkbox sits on one x-axis. */
export const WORKSPACE_CHECKBOX_CELL_LAYOUT_CLASS =
  'box-border p-0 text-center align-middle leading-none';
export const WORKSPACE_FIRST_DATA_COL_LEFT_CLASS = 'left-[44px]';
export const WORKSPACE_FROZEN_COL_FALLBACK_WIDTH_PX = 160;

/** Narrow hit strip around a column's right edge (full table height). */
const COLUMN_RESIZE_HANDLE_PX = 8;
/** Floor for resizable columns: keeps grip + title + menu usable. */
export const WORKSPACE_MIN_COLUMN_WIDTH_PX = 96;

const RESIZE_COLGROUP_ATTR = 'data-workspace-resize-cols';
const RESIZE_LAYER_ATTR = 'data-workspace-resize-layer';
const RESIZE_GUIDE_ATTR = 'data-workspace-resize-guide';
const DRAGGING_ATTR = 'data-workspace-dragging';

type ColumnWidthMap = Record<string, number>;

/** Clamps a column width to the global floor or the column's own `data-workspace-min-width`. */
export function clampColumnWidth(width: number, minWidthAttr?: string): number {
  return Math.max(WORKSPACE_MIN_COLUMN_WIDTH_PX, Number(minWidthAttr) || 0, width);
}

function headerColumnKey(th: HTMLElement, index: number): string {
  return th.dataset.workspaceColId || `__idx-${index}`;
}

function snapshotTableColumnWidths(table: HTMLTableElement, existing: ColumnWidthMap): ColumnWidthMap {
  const widths: ColumnWidthMap = { ...existing };
  table.querySelectorAll<HTMLElement>('thead th').forEach((th, index) => {
    const key = headerColumnKey(th, index);
    if (widths[key] == null) {
      widths[key] = Math.round(th.getBoundingClientRect().width);
    }
  });
  return widths;
}

function syncTableColumnWidths(table: HTMLTableElement, widths: ColumnWidthMap) {
  const headers = Array.from(table.querySelectorAll<HTMLElement>('thead th'));
  if (headers.length === 0) return;

  let colgroup = table.querySelector(`colgroup[${RESIZE_COLGROUP_ATTR}]`);
  if (!colgroup) {
    colgroup = document.createElement('colgroup');
    colgroup.setAttribute(RESIZE_COLGROUP_ATTR, '');
    table.insertBefore(colgroup, table.firstChild);
  }

  while (colgroup.children.length < headers.length) {
    colgroup.appendChild(document.createElement('col'));
  }
  while (colgroup.children.length > headers.length) {
    colgroup.lastChild?.remove();
  }

  let total = 0;
  headers.forEach((th, index) => {
    const key = headerColumnKey(th, index);
    const width = clampColumnWidth(
      widths[key] ?? Math.round(th.getBoundingClientRect().width),
      th.dataset.workspaceMinWidth
    );
    const col = colgroup!.children[index] as HTMLTableColElement;
    col.style.width = `${width}px`;
    col.style.minWidth = `${width}px`;
    col.style.maxWidth = `${width}px`;
    total += width;
  });

  table.style.tableLayout = 'fixed';
  table.style.width = `${total}px`;
  table.style.minWidth = `${total}px`;
}

function ensureResizeGuide(): HTMLDivElement {
  let guide = document.querySelector<HTMLDivElement>(`[${RESIZE_GUIDE_ATTR}]`);
  if (guide) return guide;
  guide = document.createElement('div');
  guide.setAttribute(RESIZE_GUIDE_ATTR, '');
  guide.setAttribute('aria-hidden', 'true');
  Object.assign(guide.style, {
    position: 'fixed',
    width: '1px',
    pointerEvents: 'none',
    zIndex: '10000',
    background: 'var(--brand-primary)',
    display: 'none',
  });
  document.body.appendChild(guide);
  return guide;
}

function showResizeGuide(viewport: HTMLElement, clientX: number) {
  const guide = ensureResizeGuide();
  const rect = viewport.getBoundingClientRect();
  guide.style.left = `${clientX}px`;
  guide.style.top = `${rect.top}px`;
  guide.style.height = `${Math.max(rect.height, 1)}px`;
  guide.style.display = 'block';
}

function hideResizeGuide() {
  const guide = document.querySelector<HTMLDivElement>(`[${RESIZE_GUIDE_ATTR}]`);
  if (guide) guide.style.display = 'none';
}

/** Full-height, invisible hit strips at each data-column right edge. */
function syncWorkspaceResizeHandles(
  table: HTMLTableElement,
  viewport: HTMLElement,
  onResizeStart: (columnId: string, startWidth: number, clientX: number) => void
) {
  if (getComputedStyle(viewport).position === 'static') {
    viewport.style.position = 'relative';
  }

  let layer = viewport.querySelector<HTMLDivElement>(`:scope > [${RESIZE_LAYER_ATTR}]`);
  if (!layer) {
    layer = document.createElement('div');
    layer.setAttribute(RESIZE_LAYER_ATTR, '');
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      pointerEvents: 'none',
      zIndex: '60',
    });
    viewport.appendChild(layer);
  }

  layer.style.left = `${table.offsetLeft}px`;
  layer.style.top = `${table.offsetTop}px`;
  layer.style.width = `${table.offsetWidth}px`;
  layer.style.height = `${table.offsetHeight}px`;

  const headers = Array.from(
    table.querySelectorAll<HTMLElement>('thead th[data-workspace-col-id]')
  );
  const tableRect = table.getBoundingClientRect();

  while (layer.childElementCount > headers.length) {
    layer.lastElementChild?.remove();
  }
  while (layer.childElementCount < headers.length) {
    const handle = document.createElement('div');
    handle.dataset.workspaceColResizeHandle = '';
    Object.assign(handle.style, {
      position: 'absolute',
      top: '0',
      width: `${COLUMN_RESIZE_HANDLE_PX}px`,
      height: '100%',
      pointerEvents: 'auto',
      cursor: 'col-resize',
    });
    layer.appendChild(handle);
  }

  headers.forEach((th, index) => {
    const columnId = th.dataset.workspaceColId;
    const handle = layer!.children[index] as HTMLDivElement;
    if (!columnId) {
      handle.style.display = 'none';
      return;
    }
    const thRect = th.getBoundingClientRect();
    const left = thRect.right - tableRect.left - COLUMN_RESIZE_HANDLE_PX / 2;
    handle.style.display = 'block';
    handle.style.left = `${left}px`;
    handle.onmousedown = (event) => {
      if (event.button !== 0) return;
      if (table.getAttribute(DRAGGING_ATTR)) return;
      event.preventDefault();
      event.stopPropagation();
      onResizeStart(columnId, Math.round(thRect.width), event.clientX);
    };
  });
}

/** Right-edge divider used only on the last frozen data column. */
const FROZEN_EDGE_SHADOW = 'shadow-[6px_0_10px_-8px_rgba(15,23,42,0.28)]';
/** Top-edge divider used on sticky totals-row cells (same blur/spread as the Type column). */
const FROZEN_BOTTOM_EDGE_SHADOW = 'shadow-[0_-6px_10px_-8px_rgba(15,23,42,0.28)]';
/** Last frozen column in the totals row: right-edge + upward edge. */
const FROZEN_FOOTER_CORNER_SHADOW =
  'shadow-[6px_0_10px_-8px_rgba(15,23,42,0.28),0_-6px_10px_-8px_rgba(15,23,42,0.28)]';

export type WorkspaceFrozenVariant = 'header' | 'body' | 'footer';

/**
 * Sticky `left` for a frozen data column: checkbox width plus any preceding
 * frozen columns. With a single frozen data column this is just the checkbox.
 * Pass `checkboxWidthPx = 0` for tables that have no selection column.
 */
export function workspaceFrozenLeftOffsetPx(
  precedingFrozenWidthsPx: number[] = [],
  checkboxWidthPx = WORKSPACE_CHECKBOX_COL_WIDTH_PX
): number {
  return checkboxWidthPx + precedingFrozenWidthsPx.reduce((sum, width) => sum + width, 0);
}

export function workspaceFrozenLeftByColumnId(
  frozenColumnIds: readonly string[],
  widthsPx: Record<string, number>,
  checkboxWidthPx = WORKSPACE_CHECKBOX_COL_WIDTH_PX
): Record<string, number> {
  const result: Record<string, number> = {};
  const preceding: number[] = [];
  for (const id of frozenColumnIds) {
    result[id] = workspaceFrozenLeftOffsetPx(preceding, checkboxWidthPx);
    preceding.push(widthsPx[id] ?? WORKSPACE_FROZEN_COL_FALLBACK_WIDTH_PX);
  }
  return result;
}

/** Scrolling metric headers use `workspaceTableClass.th` (`z-30`). */
const WORKSPACE_SCROLLING_HEADER_Z_INDEX = 30;
/** Frozen body/footer must stay below the header so rows scroll underneath it. */
const WORKSPACE_FROZEN_BODY_Z_INDEX = 20;
const WORKSPACE_FROZEN_FOOTER_Z_INDEX = 22;
const WORKSPACE_FROZEN_HEADER_Z_INDEX = WORKSPACE_SCROLLING_HEADER_Z_INDEX + 5;

function frozenZIndex(index: number, count: number, variant: WorkspaceFrozenVariant): number {
  const base =
    variant === 'header'
      ? WORKSPACE_FROZEN_HEADER_Z_INDEX
      : variant === 'footer'
        ? WORKSPACE_FROZEN_FOOTER_Z_INDEX
        : WORKSPACE_FROZEN_BODY_Z_INDEX;
  return base + Math.max(0, count - 1 - index);
}

function frozenBackgroundClass(variant: WorkspaceFrozenVariant): string {
  if (variant === 'header') return 'bg-surface-header';
  if (variant === 'footer') return 'bg-surface-muted';
  return 'bg-surface';
}

export function workspaceStickyCheckboxCellClass(isHeader = false): string {
  return [
    // Opaque backgrounds only — CSS-variable colors with /opacity (e.g. bg-surface-header/95)
    // resolve to transparent in this Tailwind setup, which lets scrolled cells bleed through.
    isHeader ? 'sticky top-0 left-0 bg-surface-header z-40' : 'sticky left-0 bg-surface-header z-[21]',
    isHeader ? '' : 'border-b border-surface-muted',
    WORKSPACE_CHECKBOX_CELL_LAYOUT_CLASS,
    WORKSPACE_CHECKBOX_COL_SIZE_CLASS,
  ]
    .filter(Boolean)
    .join(' ');
}

export function workspaceStickyDataCellClass(isHeader = false): string {
  return [
    isHeader
      ? `sticky top-0 ${WORKSPACE_FIRST_DATA_COL_LEFT_CLASS} z-[35] border-r border-border bg-surface-header ${FROZEN_EDGE_SHADOW}`
      : `sticky ${WORKSPACE_FIRST_DATA_COL_LEFT_CLASS} border-r border-border ${FROZEN_EDGE_SHADOW} z-[20] bg-surface`,
  ].join(' ');
}

export function workspaceColumnStickyProps(
  columnId: string,
  frozenColumnIds: readonly string[] | null | undefined,
  frozenLeftById: Record<string, number> = {},
  variant: WorkspaceFrozenVariant = 'body'
): { className: string; style?: CSSProperties } {
  const index = frozenColumnIds?.indexOf(columnId) ?? -1;
  const isFrozen = Boolean(frozenColumnIds?.length) && index >= 0;

  if (variant === 'footer' && !isFrozen) {
    return { className: workspaceStickyFooterFillClass() };
  }

  if (!isFrozen || !frozenColumnIds) return { className: '' };

  const isLastFrozen = index === frozenColumnIds.length - 1;
  const isFooter = variant === 'footer';
  const edgeShadow = isLastFrozen
    ? `border-r border-border ${isFooter ? FROZEN_FOOTER_CORNER_SHADOW : FROZEN_EDGE_SHADOW}`
    : isFooter
      ? FROZEN_BOTTOM_EDGE_SHADOW
      : '';

  return {
    className: [
      'sticky',
      variant === 'header' ? 'top-0' : '',
      isFooter ? 'bottom-0' : '',
      edgeShadow,
      frozenBackgroundClass(variant),
    ]
      .filter(Boolean)
      .join(' '),
    style: {
      left: frozenLeftById[columnId] ?? workspaceFrozenLeftOffsetPx(),
      ...(variant === 'header' ? { top: 0 } : {}),
      zIndex: frozenZIndex(index, frozenColumnIds.length, variant),
    },
  };
}

/** Sticky freeze styles for a selected column. */
export function workspaceColumnStickyClass(
  columnId: string,
  frozenColumnIds: readonly string[] | null | undefined,
  isHeader = false,
  frozenLeftById: Record<string, number> = {}
): string {
  return workspaceColumnStickyProps(
    columnId,
    frozenColumnIds,
    frozenLeftById,
    isHeader ? 'header' : 'body'
  ).className;
}

/** Non-frozen totals cells: stick to the bottom of the table viewport. */
export function workspaceStickyFooterFillClass(): string {
  return ['sticky bottom-0 z-10 bg-surface-muted', FROZEN_BOTTOM_EDGE_SHADOW].join(' ');
}

/** Footer sticky cells: opaque muted surface so Total stays readable while scrolling. */
export function workspaceStickyCheckboxFooterClass(): string {
  return [
    'sticky bottom-0 left-0 z-[23] bg-surface-muted',
    WORKSPACE_CHECKBOX_CELL_LAYOUT_CLASS,
    FROZEN_BOTTOM_EDGE_SHADOW,
    WORKSPACE_CHECKBOX_COL_SIZE_CLASS,
  ].join(' ');
}

export function workspaceStickyDataFooterClass(): string {
  return [
    `sticky bottom-0 ${WORKSPACE_FIRST_DATA_COL_LEFT_CLASS} z-[22] border-r border-border bg-surface-muted ${FROZEN_FOOTER_CORNER_SHADOW}`,
  ].join(' ');
}

export function workspaceColumnStickyFooterClass(
  columnId: string,
  frozenColumnIds: readonly string[] | null | undefined,
  frozenLeftById: Record<string, number> = {}
): string {
  return workspaceColumnStickyProps(columnId, frozenColumnIds, frozenLeftById, 'footer').className;
}

function widthsEqual(left: Record<string, number>, right: Record<string, number>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

/**
 * Measures header cell widths so stacked frozen columns can use real `left` offsets.
 * Also wires full-height column-boundary resizing on the table scroll viewport.
 */
export function useWorkspaceFrozenLayout(
  frozenColumnIds: readonly string[],
  columnSignature = ''
): {
  tableRef: (node: HTMLTableElement | null) => void;
  frozenLeftById: Record<string, number>;
} {
  const [table, setTable] = useState<HTMLTableElement | null>(null);
  const [widthsPx, setWidthsPx] = useState<Record<string, number>>({});
  const [checkboxWidthPx, setCheckboxWidthPx] = useState(WORKSPACE_CHECKBOX_COL_WIDTH_PX);
  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap>({});
  const columnWidthsRef = useRef<ColumnWidthMap>({});
  const tableRef = useCallback((node: HTMLTableElement | null) => {
    setTable(node);
  }, []);

  useLayoutEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);

  useLayoutEffect(() => {
    if (!table) return;
    if (Object.keys(columnWidths).length === 0) return;
    syncTableColumnWidths(table, columnWidths);
  }, [table, columnSignature, columnWidths]);

  useLayoutEffect(() => {
    if (!table) return;

    const measure = () => {
      const next: Record<string, number> = {};
      table.querySelectorAll<HTMLElement>('thead [data-workspace-col-id]').forEach((el) => {
        const id = el.dataset.workspaceColId;
        if (!id) return;
        next[id] = el.getBoundingClientRect().width;
      });
      setWidthsPx((prev) => (widthsEqual(prev, next) ? prev : next));

      const firstTh = table.querySelector('thead th:first-child');
      const width =
        firstTh instanceof HTMLElement
          ? firstTh.dataset.workspaceColId
            ? 0
            : Math.round(firstTh.getBoundingClientRect().width)
          : 0;
      setCheckboxWidthPx((prev) => (prev === width ? prev : width));

      if (Object.keys(columnWidthsRef.current).length > 0) {
        syncTableColumnWidths(table, columnWidthsRef.current);
      }
    };

    const resizeObserver = new ResizeObserver(measure);
    const observeHeaderCells = () => {
      table.querySelectorAll('thead th').forEach((el) => {
        resizeObserver.observe(el);
      });
      measure();
    };

    observeHeaderCells();
    const mutationObserver = new MutationObserver(() => {
      observeHeaderCells();
    });
    if (table.tHead) {
      mutationObserver.observe(table.tHead, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [table, columnSignature]);

  useLayoutEffect(() => {
    if (!table) return;
    const viewport = table.parentElement;
    if (!viewport) return;

    type DragState = { columnId: string; startX: number; startWidth: number };
    let drag: DragState | null = null;

    const refreshHandles = () => {
      syncWorkspaceResizeHandles(table, viewport, (columnId, startWidth, clientX) => {
        if (table.getAttribute(DRAGGING_ATTR)) return;
        const widths = snapshotTableColumnWidths(table, columnWidthsRef.current);
        drag = {
          columnId,
          startX: clientX,
          startWidth: widths[columnId] ?? startWidth,
        };
        columnWidthsRef.current = widths;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        showResizeGuide(viewport, clientX);
      });
    };

    refreshHandles();

    const onMouseMove = (event: MouseEvent) => {
      if (!drag) return;
      const th = table.querySelector<HTMLElement>(
        `thead th[data-workspace-col-id="${CSS.escape(drag.columnId)}"]`
      );
      const nextWidth = clampColumnWidth(
        Math.round(drag.startWidth + (event.clientX - drag.startX)),
        th?.dataset.workspaceMinWidth
      );
      const next = {
        ...columnWidthsRef.current,
        [drag.columnId]: nextWidth,
      };
      columnWidthsRef.current = next;
      syncTableColumnWidths(table, next);
      // Follow the clamped edge, not the pointer, so the guide stops at the column's minimum.
      showResizeGuide(viewport, drag.startX + (nextWidth - drag.startWidth));
      document.body.style.cursor = 'col-resize';
    };

    const onMouseUp = () => {
      if (!drag) return;
      drag = null;
      setColumnWidths({ ...columnWidthsRef.current });
      hideResizeGuide();
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      refreshHandles();
    };

    const handleObserver = new ResizeObserver(() => {
      if (drag) return;
      refreshHandles();
    });
    handleObserver.observe(table);
    viewport.addEventListener(
      'scroll',
      () => {
        if (drag) return;
        refreshHandles();
      },
      { passive: true }
    );    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    const mutationObserver = new MutationObserver(() => {
      refreshHandles();
    });
    if (table.tHead) {
      mutationObserver.observe(table.tHead, { childList: true, subtree: true });
    }

    return () => {
      handleObserver.disconnect();
      mutationObserver.disconnect();
      viewport.removeEventListener('scroll', refreshHandles);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      viewport.querySelector(`:scope > [${RESIZE_LAYER_ATTR}]`)?.remove();
      hideResizeGuide();
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };
  }, [table, columnSignature]);

  const frozenLeftById = useMemo(
    () => workspaceFrozenLeftByColumnId(frozenColumnIds, widthsPx, checkboxWidthPx),
    [frozenColumnIds, widthsPx, checkboxWidthPx]
  );

  return { tableRef, frozenLeftById };
}
